import type { SubRoute } from '@ipen-portal/plugin-common-react';
import type { JSX } from 'react';

import { isRouteWithMultipleTabs } from '@ipen-portal/plugin-common-react';
import { matchRoutes, useParams, useRoutes } from 'react-router';

// copy function from component "RoutedTabs"
export const useSelectedSubRoute = (
  subRoutes: readonly SubRoute[],
): {
  element?: JSX.Element;
  index: number;
  isFallbackRoute: boolean;
  route?: SubRoute;
  selectedSubPath?: string;
} => {
  const params = useParams();

  const routes = subRoutes.map(({ children, path }) => ({
    caseSensitive: false,
    element: children,
    path: `${path}/*`.replaceAll('//', '/'),
  }));

  const element = useRoutes(routes) ?? subRoutes[0]?.children;

  // This avoids having a double / prefix for react-router v6 beta, which in turn breaks
  // the tab highlighting when using relative paths for the tabs.
  let currentRoute = params['*'] ?? '';
  if (!currentRoute.startsWith('/')) {
    currentRoute = `/${currentRoute}`;
  }

  const [matchedRoute] =
    matchRoutes(
      routes.filter(({ element: e }) => !!e),
      currentRoute,
    ) ?? [];
  const foundIndex = matchedRoute
    ? subRoutes.findIndex(
        t => `${t.path}/*`.replaceAll('//', '/') === matchedRoute.route.path,
      )
    : 0;

  const route = subRoutes[foundIndex] ?? subRoutes[0];
  const selectedSubPath = isRouteWithMultipleTabs(route)
    ? (route.tabs.find(
        tab =>
          tab.subPath
          && currentRoute.startsWith(`${route.path}/${tab.subPath}`),
      )?.subPath ?? '')
    : undefined;
  const determinedRoute =
    route?.path + (selectedSubPath === undefined ? '' : `/${selectedSubPath}`);
  return {
    element,
    index: foundIndex === -1 ? 0 : foundIndex,
    isFallbackRoute: currentRoute !== determinedRoute,
    route,
    selectedSubPath,
  };
};
